import React, { useState, useMemo, useCallback, useEffect } from "react"
import * as Icons from "lucide-react"
import { emailApi } from "../services/emailApi"

const STORAGE_KEY = "em-calendar-events"
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]
const EVENT_COLORS = [
  "#2563eb",
  "#059669",
  "#d97706",
  "#dc2626",
  "#7c3aed",
  "#0891b2",
]

const pad = n => String(n).padStart(2, "0")
const dateKey = d =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate()

function loadEvents() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

export default function EmailCalendar({ activeAccount, showToast }) {
  const today = useMemo(() => new Date(), [])
  const [cursor, setCursor] = useState(
    new Date(today.getFullYear(), today.getMonth(), today.getDate())
  )
  const [miniCursor, setMiniCursor] = useState(
    new Date(today.getFullYear(), today.getMonth(), 1)
  )
  const [view, setView] = useState("month") // month | week | day
  const [events, setEvents] = useState(loadEvents)
  const [syncedEvents, setSyncedEvents] = useState([])
  const [calSyncing, setCalSyncing] = useState(false)
  const [modal, setModal] = useState(null) // { event, dateStr }

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(events))
    } catch {}
  }, [events])

  // Convert an Exchange calendar event into the local event shape (times shown
  // in the viewer's own timezone).
  const toLocalEvent = useCallback(ev => {
    const s = ev.start ? new Date(ev.start) : null
    if (!s || isNaN(s.getTime())) return null
    const e = ev.end ? new Date(ev.end) : null
    const hm = d => `${pad(d.getHours())}:${pad(d.getMinutes())}`
    return {
      id: "ex-" + (ev.id || `${ev.title}-${ev.start}`),
      title: ev.title || "(no title)",
      date: dateKey(s),
      start: ev.all_day ? "" : hm(s),
      end: e && !ev.all_day ? hm(e) : "",
      location: ev.location || "",
      description: ev.organizer ? `Organizer: ${ev.organizer}` : "",
      color: "#0e527d",
      synced: true,
      allDay: !!ev.all_day,
    }
  }, [])

  // Pull the Exchange calendar for the visible month (± 1 month for the grid's
  // spill-over days) whenever the month or mailbox changes.
  const monthKey = `${cursor.getFullYear()}-${cursor.getMonth()}`
  useEffect(() => {
    if (!activeAccount?.account_id) return undefined
    const y = cursor.getFullYear()
    const m = cursor.getMonth()
    const iso = d =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    const startD = new Date(y, m - 1, 1)
    const endD = new Date(y, m + 2, 0)
    let cancelled = false
    setCalSyncing(true)
    emailApi
      .getCalendarEvents({
        account: activeAccount.account_id,
        start: iso(startD),
        end: iso(endD),
      })
      .then(res => {
        if (cancelled) return
        const evs = (res.data?.events || []).map(toLocalEvent).filter(Boolean)
        setSyncedEvents(evs)
      })
      .catch(() => {
        /* calendar sync is best-effort */
      })
      .finally(() => {
        if (!cancelled) setCalSyncing(false)
      })
    return () => {
      cancelled = true
    }
  }, [activeAccount, monthKey, toLocalEvent])

  const allEvents = useMemo(
    () => [...events, ...syncedEvents],
    [events, syncedEvents]
  )

  const eventsForDay = useCallback(
    d =>
      allEvents
        .filter(ev => ev.date === dateKey(d))
        .sort((a, b) => (a.start || "").localeCompare(b.start || "")),
    [allEvents]
  )

  const openNewEvent = useCallback(d => {
    setModal({
      dateStr: dateKey(d),
      event: {
        id: null,
        title: "",
        date: dateKey(d),
        start: "09:00",
        end: "10:00",
        location: "",
        description: "",
        color: EVENT_COLORS[0],
        allDay: false,
      },
    })
  }, [])

  const openEditEvent = useCallback(ev => {
    setModal({ dateStr: ev.date, event: { ...ev } })
  }, [])

  const saveEvent = useCallback(ev => {
    setEvents(prev => {
      if (ev.id) return prev.map(e => (e.id === ev.id ? ev : e))
      return [
        ...prev,
        { ...ev, id: `evt-${Date.now()}-${Math.floor(Math.random() * 1000)}` },
      ]
    })
    setModal(null)
  }, [])

  const deleteEvent = useCallback(id => {
    setEvents(prev => prev.filter(e => e.id !== id))
    setModal(null)
  }, [])

  // ---------- Header navigation ----------
  const shift = amount => {
    const d = new Date(cursor)
    if (view === "month") d.setMonth(d.getMonth() + amount)
    else if (view === "week") d.setDate(d.getDate() + amount * 7)
    else d.setDate(d.getDate() + amount)
    setCursor(d)
    setMiniCursor(new Date(d.getFullYear(), d.getMonth(), 1))
  }

  const goToday = () => {
    const d = new Date(today.getFullYear(), today.getMonth(), today.getDate())
    setCursor(d)
    setMiniCursor(new Date(d.getFullYear(), d.getMonth(), 1))
  }

  const headerLabel = () => {
    if (view === "day") {
      return `${WEEKDAYS[cursor.getDay()]}, ${
        MONTHS[cursor.getMonth()]
      } ${cursor.getDate()}, ${cursor.getFullYear()}`
    }
    if (view === "week") {
      const start = new Date(cursor)
      start.setDate(cursor.getDate() - cursor.getDay())
      const end = new Date(start)
      end.setDate(start.getDate() + 6)
      return `${MONTHS[start.getMonth()].slice(
        0,
        3
      )} ${start.getDate()} – ${MONTHS[end.getMonth()].slice(
        0,
        3
      )} ${end.getDate()}, ${end.getFullYear()}`
    }
    return `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`
  }

  // ---------- Mini calendar ----------
  const miniGrid = useMemo(() => {
    const firstDay = new Date(
      miniCursor.getFullYear(),
      miniCursor.getMonth(),
      1
    )
    const start = new Date(firstDay)
    start.setDate(1 - firstDay.getDay())
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start)
      d.setDate(start.getDate() + i)
      return d
    })
  }, [miniCursor])

  // ---------- Month grid ----------
  const monthGrid = useMemo(() => {
    const firstDay = new Date(cursor.getFullYear(), cursor.getMonth(), 1)
    const start = new Date(firstDay)
    start.setDate(1 - firstDay.getDay())
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start)
      d.setDate(start.getDate() + i)
      return d
    })
  }, [cursor])

  const weekDays = useMemo(() => {
    const start = new Date(cursor)
    start.setDate(cursor.getDate() - cursor.getDay())
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start)
      d.setDate(start.getDate() + i)
      return d
    })
  }, [cursor])

  return (
    <div className="em-cal">
      {calSyncing && (
        <div className="em-cal-syncing" role="status">
          <Icons.Loader2 size={14} className="em-spin" />
          <span>Syncing your calendar…</span>
        </div>
      )}
      {/* Sidebar */}
      <div className="em-cal-sidebar">
        <button
          className="em-sidebar-newmail em-cal-new-btn"
          onClick={() => openNewEvent(cursor)}
        >
          <Icons.Plus size={16} />
          <span>New event</span>
        </button>

        <div className="em-cal-mini">
          <div className="em-cal-mini-header">
            <span>
              {MONTHS[miniCursor.getMonth()]} {miniCursor.getFullYear()}
            </span>
            <div className="em-cal-mini-nav">
              <button
                onClick={() =>
                  setMiniCursor(
                    new Date(
                      miniCursor.getFullYear(),
                      miniCursor.getMonth() - 1,
                      1
                    )
                  )
                }
              >
                <Icons.ChevronLeft size={14} />
              </button>
              <button
                onClick={() =>
                  setMiniCursor(
                    new Date(
                      miniCursor.getFullYear(),
                      miniCursor.getMonth() + 1,
                      1
                    )
                  )
                }
              >
                <Icons.ChevronRight size={14} />
              </button>
            </div>
          </div>
          <div className="em-cal-mini-grid">
            {WEEKDAYS.map(w => (
              <div key={w} className="em-cal-mini-dow">
                {w[0]}
              </div>
            ))}
            {miniGrid.map((d, i) => {
              const isCur = d.getMonth() === miniCursor.getMonth()
              const isToday = sameDay(d, today)
              const isSel = sameDay(d, cursor)
              const has = eventsForDay(d).length > 0
              return (
                <button
                  key={i}
                  className={`em-cal-mini-day ${
                    isCur ? "" : "em-cal-mini-day-muted"
                  } ${isToday ? "em-cal-mini-day-today" : ""} ${
                    isSel ? "em-cal-mini-day-sel" : ""
                  }`}
                  onClick={() => {
                    setCursor(new Date(d))
                  }}
                >
                  {d.getDate()}
                  {has && <span className="em-cal-mini-dot" />}
                </button>
              )
            })}
          </div>
        </div>

        <div className="em-cal-upcoming">
          <div className="em-sidebar-section-title">Upcoming</div>
          {events
            .filter(
              ev =>
                new Date(`${ev.date}T${ev.start || "00:00"}`) >=
                new Date(today.getFullYear(), today.getMonth(), today.getDate())
            )
            .sort((a, b) =>
              `${a.date}T${a.start}`.localeCompare(`${b.date}T${b.start}`)
            )
            .slice(0, 6)
            .map(ev => (
              <div
                key={ev.id}
                className="em-cal-upcoming-item"
                onClick={() => openEditEvent(ev)}
              >
                <span
                  className="em-cal-upcoming-dot"
                  style={{ background: ev.color }}
                />
                <div className="em-cal-upcoming-info">
                  <div className="em-cal-upcoming-title">
                    {ev.title || "(No title)"}
                  </div>
                  <div className="em-cal-upcoming-time">
                    {ev.date}
                    {ev.allDay ? " · All day" : ` · ${ev.start}`}
                  </div>
                </div>
              </div>
            ))}
          {events.length === 0 && (
            <div className="em-cal-upcoming-empty">No upcoming events</div>
          )}
        </div>
      </div>

      {/* Main */}
      <div className="em-cal-main">
        <div className="em-cal-toolbar">
          <button className="em-cal-today-btn" onClick={goToday}>
            Today
          </button>
          <div className="em-cal-nav">
            <button onClick={() => shift(-1)} title="Previous">
              <Icons.ChevronLeft size={18} />
            </button>
            <button onClick={() => shift(1)} title="Next">
              <Icons.ChevronRight size={18} />
            </button>
          </div>
          <div className="em-cal-title">{headerLabel()}</div>
          <div className="em-cal-view-switch">
            {["day", "week", "month"].map(v => (
              <button
                key={v}
                className={view === v ? "em-cal-view-active" : ""}
                onClick={() => setView(v)}
              >
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {view === "month" && (
          <div className="em-cal-month">
            <div className="em-cal-month-dow">
              {WEEKDAYS.map(w => (
                <div key={w} className="em-cal-month-dow-cell">
                  {w}
                </div>
              ))}
            </div>
            <div className="em-cal-month-grid">
              {monthGrid.map((d, i) => {
                const isCur = d.getMonth() === cursor.getMonth()
                const isToday = sameDay(d, today)
                const dayEvents = eventsForDay(d)
                return (
                  <div
                    key={i}
                    className={`em-cal-cell ${
                      isCur ? "" : "em-cal-cell-muted"
                    } ${isToday ? "em-cal-cell-today" : ""}`}
                    onClick={() => openNewEvent(d)}
                  >
                    <div className="em-cal-cell-date">
                      {d.getDate() === 1 && (
                        <span className="em-cal-cell-month">
                          {MONTHS[d.getMonth()].slice(0, 3)}{" "}
                        </span>
                      )}
                      <span className={isToday ? "em-cal-cell-today-num" : ""}>
                        {d.getDate()}
                      </span>
                    </div>
                    <div className="em-cal-cell-events">
                      {dayEvents.slice(0, 3).map(ev => (
                        <div
                          key={ev.id}
                          className="em-cal-event-chip"
                          style={{ background: ev.color }}
                          onClick={e => {
                            e.stopPropagation()
                            openEditEvent(ev)
                          }}
                          title={ev.title}
                        >
                          {!ev.allDay && (
                            <span className="em-cal-event-time">
                              {ev.start}
                            </span>
                          )}{" "}
                          {ev.title || "(No title)"}
                        </div>
                      ))}
                      {dayEvents.length > 3 && (
                        <div className="em-cal-event-more">
                          +{dayEvents.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {view === "week" && (
          <div className="em-cal-week">
            {weekDays.map((d, i) => {
              const isToday = sameDay(d, today)
              const dayEvents = eventsForDay(d)
              return (
                <div
                  key={i}
                  className={`em-cal-week-col ${
                    isToday ? "em-cal-week-col-today" : ""
                  }`}
                >
                  <div
                    className="em-cal-week-head"
                    onClick={() => {
                      setCursor(new Date(d))
                      setView("day")
                    }}
                  >
                    <span className="em-cal-week-dow">
                      {WEEKDAYS[d.getDay()]}
                    </span>
                    <span
                      className={`em-cal-week-num ${
                        isToday ? "em-cal-cell-today-num" : ""
                      }`}
                    >
                      {d.getDate()}
                    </span>
                  </div>
                  <div
                    className="em-cal-week-body"
                    onClick={() => openNewEvent(d)}
                  >
                    {dayEvents.map(ev => (
                      <div
                        key={ev.id}
                        className="em-cal-week-event"
                        style={{ borderLeftColor: ev.color }}
                        onClick={e => {
                          e.stopPropagation()
                          openEditEvent(ev)
                        }}
                      >
                        <div className="em-cal-week-event-time">
                          {ev.allDay ? "All day" : `${ev.start} – ${ev.end}`}
                        </div>
                        <div className="em-cal-week-event-title">
                          {ev.title || "(No title)"}
                        </div>
                      </div>
                    ))}
                    {dayEvents.length === 0 && (
                      <div className="em-cal-week-empty">+ Add</div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {view === "day" && (
          <div className="em-cal-day">
            <div
              className="em-cal-day-add"
              onClick={() => openNewEvent(cursor)}
            >
              <Icons.Plus size={14} /> Add event on this day
            </div>
            {eventsForDay(cursor).length === 0 && (
              <div className="em-cal-day-empty">
                <Icons.CalendarDays size={40} strokeWidth={1} />
                <p>No events scheduled</p>
              </div>
            )}
            {eventsForDay(cursor).map(ev => (
              <div
                key={ev.id}
                className="em-cal-day-event"
                onClick={() => openEditEvent(ev)}
              >
                <div className="em-cal-day-event-time">
                  <span style={{ color: ev.color, fontWeight: 600 }}>
                    {ev.allDay ? "All day" : ev.start}
                  </span>
                  {!ev.allDay && (
                    <span className="em-cal-day-event-end">{ev.end}</span>
                  )}
                </div>
                <div
                  className="em-cal-day-event-bar"
                  style={{ background: ev.color }}
                />
                <div className="em-cal-day-event-info">
                  <div className="em-cal-day-event-title">
                    {ev.title || "(No title)"}
                  </div>
                  {ev.location && (
                    <div className="em-cal-day-event-loc">
                      <Icons.MapPin size={12} /> {ev.location}
                    </div>
                  )}
                  {ev.description && (
                    <div className="em-cal-day-event-desc">
                      {ev.description}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {modal && (
        <EventModal
          data={modal.event}
          onSave={saveEvent}
          onDelete={deleteEvent}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  )
}

function EventModal({ data, onSave, onDelete, onClose }) {
  const [form, setForm] = useState(data)
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }))

  return (
    <div className="em-search-overlay" onClick={onClose}>
      <div className="em-cal-modal" onClick={e => e.stopPropagation()}>
        <div className="em-cal-modal-header">
          <h3>{form.id ? "Edit event" : "New event"}</h3>
          <button className="em-search-close" onClick={onClose}>
            <Icons.X size={18} />
          </button>
        </div>
        <div className="em-cal-modal-body">
          <div className="em-cal-modal-field">
            <Icons.Type size={16} />
            <input
              autoFocus
              placeholder="Add a title"
              value={form.title}
              onChange={e => set("title", e.target.value)}
            />
          </div>

          <div className="em-cal-modal-field">
            <Icons.Clock size={16} />
            <input
              type="date"
              value={form.date}
              onChange={e => set("date", e.target.value)}
            />
            {!form.allDay && (
              <>
                <input
                  type="time"
                  value={form.start}
                  onChange={e => set("start", e.target.value)}
                />
                <span className="em-cal-modal-dash">–</span>
                <input
                  type="time"
                  value={form.end}
                  onChange={e => set("end", e.target.value)}
                />
              </>
            )}
          </div>

          <label className="em-cal-modal-allday">
            <input
              type="checkbox"
              checked={form.allDay}
              onChange={e => set("allDay", e.target.checked)}
            />
            All day
          </label>

          <div className="em-cal-modal-field">
            <Icons.MapPin size={16} />
            <input
              placeholder="Add a location"
              value={form.location}
              onChange={e => set("location", e.target.value)}
            />
          </div>

          <div className="em-cal-modal-field em-cal-modal-field-top">
            <Icons.AlignLeft size={16} />
            <textarea
              placeholder="Add a description"
              value={form.description}
              rows={3}
              onChange={e => set("description", e.target.value)}
            />
          </div>

          <div className="em-cal-modal-field">
            <Icons.Palette size={16} />
            <div className="em-cal-modal-colors">
              {EVENT_COLORS.map(c => (
                <button
                  key={c}
                  className={`em-cal-color-swatch ${
                    form.color === c ? "em-cal-color-swatch-active" : ""
                  }`}
                  style={{ background: c }}
                  onClick={() => set("color", c)}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="em-cal-modal-footer">
          {form.id && (
            <button
              className="em-cal-modal-delete"
              onClick={() => onDelete(form.id)}
            >
              <Icons.Trash2 size={14} /> Delete
            </button>
          )}
          <div className="em-cal-modal-footer-right">
            <button className="em-search-clear-btn" onClick={onClose}>
              Cancel
            </button>
            <button
              className="em-compose-send-btn"
              onClick={() => onSave(form)}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
